import { useAuth0 } from "@auth0/auth0-react";
import { useSupabaseLoader } from "../../../../../server/supabase/hooks";
import type { UUID } from "../../../../../utils/uuid";
import {
  type Permissions,
  type PersonDetails,
  personLabel,
  SupabasePermissionsService,
  SupabasePersonDetailsService,
} from "../../../../employees/server/supabase-person-service";

export const REVIEWER_GROUPS = {
  admins: "Admins",
  contributors: "Contributors with access to this program",
  reviewers: "Reviewers",
} as const;

type ReviewerGroup = (typeof REVIEWER_GROUPS)[keyof typeof REVIEWER_GROUPS];

export type ReviewerOption = PersonDetails & { group: ReviewerGroup };

const groupOrder: ReviewerGroup[] = [
  REVIEWER_GROUPS.admins,
  REVIEWER_GROUPS.contributors,
  REVIEWER_GROUPS.reviewers,
];

const buildReviewerOptions = (
  employees: PersonDetails[],
  permissions: Permissions[],
  programId: UUID,
  currentEmail: string,
): ReviewerOption[] => {
  const employeesById = new Map(employees.map((employee) => [employee.id, employee]));
  const optionsById = new Map<UUID, ReviewerOption>();

  const isMe = ({ email }: PersonDetails): boolean => email?.toLowerCase() === currentEmail;

  const add = (permission: Permissions, group: ReviewerGroup): void => {
    if (!permission.id || optionsById.has(permission.id)) return;
    const employee = employeesById.get(permission.id);
    // Nobody can send a Communication to themselves for review.
    if (employee && !isMe(employee)) optionsById.set(permission.id, { ...employee, group });
  };

  permissions
    .filter(({ super_admin }) => super_admin)
    .forEach((p) => add(p, REVIEWER_GROUPS.admins));

  permissions
    .filter(
      ({ super_admin, reviewer, editable_program, read_only_program }) =>
        !super_admin &&
        !reviewer &&
        (editable_program?.includes(programId) || read_only_program?.includes(programId)),
    )
    .forEach((p) => add(p, REVIEWER_GROUPS.contributors));

  permissions
    .filter(({ super_admin, reviewer }) => !super_admin && reviewer)
    .forEach((p) => add(p, REVIEWER_GROUPS.reviewers));

  return [...optionsById.values()].sort((left, right) => {
    const groupDifference = groupOrder.indexOf(left.group) - groupOrder.indexOf(right.group);
    return groupDifference || personLabel(left).localeCompare(personLabel(right));
  });
};

export const useReviewerOptions = (
  programId: UUID,
): ReturnType<typeof useSupabaseLoader<ReviewerOption[]>> => {
  // Since useClaims doesn't return a user id or a email we use useAuth0()
  // The JWT has no person id on it, but we do get an email which we can use to filer the employee list.
  const currentEmail = useAuth0().user?.email?.toLowerCase();

  return useSupabaseLoader(
    async ({ supabase, account_id }) => {
      if (!currentEmail) return [];

      const [
        { data: employees, error: employeesError },
        { data: permissions, error: permissionsError },
      ] = await Promise.all([
        new SupabasePersonDetailsService(supabase).getAll(account_id, {
          order: [{ column: "id" }],
        }),
        new SupabasePermissionsService(supabase).getAll(account_id, {
          order: [{ column: "id" }],
        }),
      ]);

      if (employeesError) throw employeesError;
      if (permissionsError) throw permissionsError;

      return buildReviewerOptions(employees ?? [], permissions ?? [], programId, currentEmail);
    },
    [programId, currentEmail],
  );
};
